import { Controller, useFormContext, useWatch } from "react-hook-form";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";

import { Panel } from "src/components/Panel/Panel.tsx";
import { useBoolean } from "src/hooks/use-boolean.ts";
import {
  PriceListItemCalculateType,
  type PriceListResource,
  type ProductPriceListItem,
  type VariantResource,
} from "@exonn/js-sdk";
import { Accordion } from "@radix-ui/react-accordion";

import { useList } from "src/system/useList.tsx";
import { ResourceEnum } from "src/constants/constants.ts";

import { FormField } from "src/components/Form/fields/FormField.tsx";

import { PriceListRow } from "src/components/Form/panels/PricePanel/components/variant/PriceListRow/PriceListRow.tsx";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { SetPriceListRow } from "src/components/Form/panels/PricePanel/components/SetPriceListRow/SetPriceListRow.tsx";
import React from "react";
import { Flex } from "src/components/Flex.tsx";
import { MiniBindButton } from "src/admin/modules/product/components/BindButton.tsx";
import type { FormValues } from "src/admin/forms/variant/config.tsx";
import { SelectPriceLists } from "src/admin/modals/product/SelectPriceLists/SelectPriceLists.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useResourceFormAction } from "src/components/Form/hooks/useResourceFormAction.ts";

export function Price() {
  const { t } = useTranslation(TranslationNamespaceEnum.PricePanel);
  const { control } = useFormContext<FormValues>();

  return (
    <Panel>
      <Panel.Header>
        <Flex gap={"10px"} alignItems="center">
          <Panel.Title>{t("panel.title")}</Panel.Title>
          <BindPrice />
        </Flex>
      </Panel.Header>
      <Panel.Content>
        <div css={{ display: "flex", gap: 10 }}>
          <FormField name={"price"} control={control} />
          <FormField control={control} name="recommended_retail_price" />
        </div>
        <PriceLists />
      </Panel.Content>
    </Panel>
  );
}

const PriceLists = () => {
  const formType = useResourceFormAction();
  const { control } = useFormContext<FormValues>();

  const isProductSet = useWatch({
    control,
    name: "is_product_set",
  });
  if (isProductSet && formType === "create") {
    return null;
  }
  if (isProductSet) {
    return <SetPriceLists />;
  }
  return <NotSetPriceLists />;
};

const BindPrice = () => {
  const { control } = useFormContext<FormValues>();
  const is_product_set = useWatch({
    name: "is_product_set",
    control,
  });
  if (is_product_set) {
    return null;
  }
  return (
    <Controller
      render={({ field: { value, onChange } }) => {
        return (
          <MiniBindButton isBound={value} onClick={() => onChange(!value)} />
        );
      }}
      name={"price_inherited"}
    />
  );
};

const SetPriceLists = () => {
  const { data } = useList<PriceListResource>({
    resource: ResourceEnum.PriceList,
    include: ["currency"],
    queryOptions: {
      refetchOnMount: true,
    },
  });
  const priceLists = data?.data || [];

  const formData = useResourceFormData<{
    data: VariantResource;
  }>();
  const variant = formData?.data;

  const assignedPriceLists = priceLists.filter((priceList) => {
    if (priceList.calculate_type !== null) {
      return true;
    }
    return variant?.components!.some((component) => {
      return component.priceListItems!.some(
        (item) => item.priceList!.id === priceList.id,
      );
    });
  });
  return (
    <>
      {variant && !!priceLists.length && !!assignedPriceLists.length && (
        <Accordion type="multiple">
          {assignedPriceLists.map(({ id }) => {
            return <SetPriceListRow variant={variant} key={id} id={id} />;
          })}
        </Accordion>
      )}
    </>
  );
};

const NotSetPriceLists = () => {
  const {
    value: modalOpen,
    setFalse: closeModal,
    setTrue: openModal,
  } = useBoolean(false);
  const { t } = useTranslation(TranslationNamespaceEnum.PricePanel);
  const { control, getValues, setValue } = useFormContext<FormValues>();

  const { data, isLoading: isPriceListLoading } = useList<PriceListResource>({
    resource: ResourceEnum.PriceList,
    include: ["currency"],
    queryOptions: {
      refetchOnMount: true,
    },
  });
  const priceInherited = useWatch({
    name: "price_inherited",
    control,
  });
  const priceLists = data?.data || [];
  const priceListItems = useWatch({ control, name: "priceListItems" });

  const assignedPriceLists = priceLists.filter((priceList) => {
    if (priceListItems[priceList.id]) {
      return true;
    }
    return priceList.calculate_type !== null;
  });

  const handleSave = ({
    tempSelectedPriceListIds,
  }: {
    tempSelectedPriceListIds: string[];
  }) => {
    const { priceListItems } = getValues();

    const nextPriceListItems = priceLists.reduce(
      (acc, priceList) => {
        const isAutomaticPriceList = priceList.calculate_type !== null;
        const isSelected = tempSelectedPriceListIds.includes(priceList.id);
        const existingPriceListItem = priceListItems?.[priceList.id];

        if (isAutomaticPriceList && !isSelected) {
          return {
            ...acc,
            [priceList.id]: {
              calculate_type: PriceListItemCalculateType.Inactive,
              calculate_value: "0",
              calculate_from: "selling_price",
              is_calculate_round: false,
            },
          };
        }

        if (!isAutomaticPriceList && isSelected) {
          if (
            existingPriceListItem &&
            existingPriceListItem.calculate_type !==
              PriceListItemCalculateType.Inactive
          ) {
            return {
              ...acc,
              [priceList.id]: existingPriceListItem,
            };
          }
          return {
            ...acc,
            [priceList.id]: {
              calculate_type: PriceListItemCalculateType.Absolute,
              calculate_value: "0",
              calculate_from: "selling_price",
              is_calculate_round: false,
            },
          };
        }

        if (!isAutomaticPriceList && !isSelected && existingPriceListItem) {
          return {
            ...acc,
            [priceList.id]: {
              calculate_type: PriceListItemCalculateType.Inactive,
              calculate_value: "0",
              calculate_from: "selling_price",
              is_calculate_round: false,
            },
          };
        }

        return acc;
      },
      {} as { [id: string]: ProductPriceListItem },
    );

    setValue("priceListItems", nextPriceListItems, {
      shouldDirty: true,
    });
    closeModal();
  };

  const selectedPriceLists = priceLists.filter((priceList) => {
    if (priceListItems[priceList.id]) {
      return (
        priceListItems[priceList.id].calculate_type !==
        PriceListItemCalculateType.Inactive
      );
    }

    return priceList.calculate_type !== null;
  });

  const selectedPriceListIds = selectedPriceLists.map(
    (priceList) => priceList.id,
  );

  return (
    <>
      {!!priceLists.length && !!assignedPriceLists.length && (
        <Accordion type="multiple">
          {assignedPriceLists.map(({ id }) => {
            return <PriceListRow key={id} id={id} />;
          })}
        </Accordion>
      )}

      {!isPriceListLoading && (
        <ButtonV2
          css={{ alignSelf: "flex-start", marginTop: 5 }}
          size="sm"
          disabled={priceInherited}
          startAdornment={<i className="icon-15-price_lists" />}
          onClick={openModal}
        >
          {t("panel.add_button")}
        </ButtonV2>
      )}

      {modalOpen && (
        <SelectPriceLists
          selectedPriceListIds={selectedPriceListIds}
          onSave={handleSave}
          priceLists={priceLists}
          onClose={closeModal}
        />
      )}
    </>
  );
};
